import React, {useState, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import * as XLSX from 'xlsx';
import TextEllipsis from '../../../components/TextEllipsis/TextEllipsis';

const MaximizedPopup = ({widgets, activeWidgetKey, snapshotData, onWidgetChange, onClose, fileMakerURL, qcOnDemandBaseUrl}) => {
  const openedAtMoment = useMemo(() => moment(), []);
  const openedAt = openedAtMoment.format('ddd, MMM D, YYYY h:mm:ss A');
  const activeWidget = widgets.find(w => w.key === activeWidgetKey);
  const activeData = snapshotData[activeWidgetKey] || [];

  // Format timestamp to datetime string for Excel export
  const formatTimestampForExport = (value) => {
    if (!value) return '';
    if (typeof value === 'number') {
      // Unix timestamp (seconds)
      return moment.unix(value).format('YYYY-MM-DD HH:mm:ss');
    }
    // Try to parse as date string
    const date = moment(value);
    if (date.isValid()) {
      return date.format('YYYY-MM-DD HH:mm:ss');
    }
    return value;
  };

  const handleExport = useCallback(() => {
    const workbook = XLSX.utils.book_new();

    widgets.forEach(widget => {
      const data = snapshotData[widget.key] || [];
      const {columns, pathField, pathField2} = widget;

      // Build header row from column labels + path fields
      const headers = columns.map(col => col.label);
      if (pathField) headers.push('FilePath');
      if (pathField2) headers.push('WO Title');

      // Build data rows
      const rows = data.map(row => {
        const rowData = columns.map(col => {
          const value = row[col.key];
          if (value === undefined || value === null) return '';
          // Format datetime columns (columns with render function are date columns)
          if (col.render) {
            return formatTimestampForExport(value);
          }
          return value;
        });
        if (pathField) rowData.push(row[pathField] || '');
        if (pathField2) rowData.push(row[pathField2] || '');
        return rowData;
      });

      // Create worksheet with headers + data
      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Calculate column widths (hybrid approach: auto-fit with min/max limits)
      const colWidths = headers.map((header, colIdx) => {
        let maxLen = header.length;

        rows.forEach(row => {
          const cellValue = String(row[colIdx] || '');
          maxLen = Math.max(maxLen, cellValue.length);
        });

        // Special handling for path columns (last 1-2 columns)
        const isPathColumn = (pathField && colIdx === headers.length - (pathField2 ? 2 : 1)) ||
                             (pathField2 && colIdx === headers.length - 1);

        if (isPathColumn) {
          return { wch: Math.min(maxLen + 2, 80) };
        }

        return { wch: Math.max(10, Math.min(maxLen + 2, 40)) };
      });
      worksheet['!cols'] = colWidths;

      // Add worksheet to workbook (sheet name max 31 chars)
      const sheetName = widget.title.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    // Generate filename with datetime
    const filename = `MamStatusDashboard_${openedAtMoment.format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [widgets, snapshotData, openedAtMoment]);

  const [popupRow, setPopupRow] = useState(null);

  if (!activeWidget) return null;

  const {columns, pathField, pathField2} = activeWidget;

  // Calculate column widths
  const getColumnWidth = (col) => {
    if (col.width) {
      return col.width;
    }
    const fixedWidthSum = columns.reduce((sum, c) => sum + (c.width || 0), 0);
    const flexColumns = columns.filter(c => !c.width).length;
    return flexColumns > 0 ? (100 - fixedWidthSum) / flexColumns : 0;
  };

  // Get urgency color based on urgency level
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 0: return '#dc3545'; // Overdue
      case 1: return '#fd7e14'; // < 6h
      case 2: return '#ffc107'; // < 24h
      case 3: return '#6c757d'; // > 24h
      default: return null;
    }
  };

  const navigateToOption = (option, row) => {
    if (option === 'qod' && qcOnDemandBaseUrl && row.wo_memfis) {
      window.open(`${qcOnDemandBaseUrl}/requests/wo/${row.wo_memfis}`, '_blank');
    } else if (option === 'memfis' && fileMakerURL && row.wo_memfis) {
      window.open(fileMakerURL.replace('@MEMFIS_WO@', row.wo_memfis), '_blank');
    } else if (option === 'action' && row.action_id) {
      window.open(`/storage?action_id=${row.action_id}`, '_blank');
    }
  };

  // Handle row click - show popup with navigation options, or navigate directly if only one option
  const handleRowClick = (e, row) => {
    if (!row.action_id && !row.wo_memfis) return;
    e.stopPropagation();

    const options = [];
    if (qcOnDemandBaseUrl && row.wo_memfis) options.push('qod');
    if (fileMakerURL && row.wo_memfis) options.push('memfis');
    if (row.action_id) options.push('action');

    if (options.length === 1) {
      navigateToOption(options[0], row);
    } else if (options.length > 1) {
      setPopupRow(row);
    }
  };

  const handlePopupOptionClick = (option) => {
    if (!popupRow) return;
    navigateToOption(option, popupRow);
    setPopupRow(null);
  };

  const closePopup = () => setPopupRow(null);

  return (
    <Overlay onClick={onClose}>
      <Popup onClick={e => e.stopPropagation()}>
        <Header>
          <TabsContainer>
            {widgets.map(widget => (
              <Tab
                key={widget.key}
                $active={widget.key === activeWidgetKey}
                onClick={() => onWidgetChange(widget.key)}
              >
                {widget.title}
              </Tab>
            ))}
          </TabsContainer>
          <OpenedDateTime>{openedAt}</OpenedDateTime>
          <ExportButton onClick={handleExport} title="Export to Excel">
            <i className="fa fa-download" />
          </ExportButton>
          <CloseButton onClick={onClose} title="Close">
            <i className="fa fa-times" />
          </CloseButton>
        </Header>
        <Content>
          <Table>
            <thead>
              <tr>
                <th>
                  <RowColumns>
                    {columns.map(col => (
                      <RowCell key={col.key} $width={getColumnWidth(col)} $maxWidth={col.maxWidth} $flex={!col.width}>{col.label}</RowCell>
                    ))}
                  </RowColumns>
                </th>
              </tr>
            </thead>
            <tbody>
              {activeData.length === 0 ? (
                <tr>
                  <td className="empty-row">
                    No data available
                  </td>
                </tr>
              ) : (
                activeData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    className={(row.action_id || row.wo_memfis) ? 'clickable' : ''}
                    title={(row.action_id || row.wo_memfis) ? 'Click to view WO or action details' : ''}
                    onClick={(e) => handleRowClick(e, row)}
                  >
                    <td>
                      <RowContainer>
                        <RowColumns>
                          {columns.map(col => (
                            <RowCell
                              key={col.key}
                              $width={getColumnWidth(col)}
                              $maxWidth={col.maxWidth}
                              $flex={!col.width}
                              $bgColor={col.key === 'wo_memfis' ? getUrgencyColor(row.urgency) : null}
                            >
                              {col.render ? col.render(row[col.key], row) : row[col.key]}
                            </RowCell>
                          ))}
                        </RowColumns>
                        {(pathField && row[pathField]) || (pathField2 && row[pathField2]) ? (
                          <RowPathContainer>
                            {pathField && row[pathField] && (
                              <RowPath>
                                <TextEllipsis
                                  data={row[pathField]}
                                  useMonospaceFont={true}
                                  noTooltip={true}
                                />
                              </RowPath>
                            )}
                            {pathField2 && row[pathField2] && (
                              <RowPath>
                                <TextEllipsis
                                  data={row[pathField2]}
                                  useMonospaceFont={true}
                                  noTooltip={true}
                                />
                              </RowPath>
                            )}
                          </RowPathContainer>
                        ) : null}
                      </RowContainer>
                    </td>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </Content>
        {popupRow && (
          <PopupOverlay onClick={closePopup}>
            <PopupMenu onClick={e => e.stopPropagation()}>
              <PopupTitle>What do you want to open?</PopupTitle>
              {qcOnDemandBaseUrl && popupRow.wo_memfis && (
                <PopupOption onClick={() => handlePopupOptionClick('qod')}>WO in QOD</PopupOption>
              )}
              {fileMakerURL && popupRow.wo_memfis && (
                <PopupOption onClick={() => handlePopupOptionClick('memfis')}>WO in MEMFIS</PopupOption>
              )}
              {popupRow.action_id && (
                <PopupOption onClick={() => handlePopupOptionClick('action')}>MAM Action</PopupOption>
              )}
            </PopupMenu>
          </PopupOverlay>
        )}
      </Popup>
    </Overlay>
  );
};

MaximizedPopup.propTypes = {
  widgets: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    columns: PropTypes.array.isRequired,
    pathField: PropTypes.string,
    pathField2: PropTypes.string
  })).isRequired,
  activeWidgetKey: PropTypes.string.isRequired,
  snapshotData: PropTypes.object.isRequired,
  onWidgetChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  fileMakerURL: PropTypes.string,
  qcOnDemandBaseUrl: PropTypes.string
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Popup = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  background: #343a40;
  color: #fff;
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px 8px 0 0;
  min-height: 50px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 8px 0;
`;

const Tab = styled.button`
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.25)' : 'transparent'};
  border: none;
  color: #fff;
  font-size: 1.4rem;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }

  &:active {
    background: rgba(255, 255, 255, 0.45);
  }
`;

const OpenedDateTime = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  white-space: nowrap;
  margin-left: auto;
  margin-right: 16px;
`;

const ExportButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  font-size: 1.2rem;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  font-size: 1.2rem;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;
  margin-left: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const Content = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 1;

    th {
      border-bottom: 2px solid #dee2e6;
      font-weight: bold;
      text-align: left;
      white-space: normal;
      word-wrap: break-word;
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: #f8f9fa;
      }

      &:not(:last-child) td {
        border-bottom: 1px solid #dee2e6;
      }

      &.clickable {
        cursor: pointer;

        &:hover {
          background: #e2e6ea;
        }
      }
    }

    td {
      padding: 0;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;

      &.empty-row {
        text-align: center;
        color: #6c757d;
        padding: 24px;
      }
    }
  }
`;

const TableRow = styled.tr``;

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowColumns = styled.div`
  display: flex;
  padding: 8px 0;
`;

const RowCell = styled.div`
  flex: ${props => props.$flex ? '1 1' : '0 0'} ${props => props.$width}%;
  width: ${props => props.$width}%;
  ${props => props.$maxWidth ? `max-width: ${props.$maxWidth}px;` : ''}
  box-sizing: border-box;
  padding: 0 7px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  ${props => props.$bgColor ? `
    background-color: ${props.$bgColor};
    color: #fff;
    font-weight: bold;
  ` : ''}
`;

const RowPathContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowPath = styled.div`
  padding: 0 12px 2px 12px;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  white-space: nowrap;
  font-size: 0.85em;
  color: #6c757d;
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const PopupMenu = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  min-width: 250px;
  padding: 8px 0;
`;

const PopupTitle = styled.div`
  padding: 8px 16px;
  font-size: 1.5rem;
  color: #6c757d;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 4px;
`;

const PopupOption = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 1.3rem;
  color: #212529;

  &:hover {
    background: #f0f0f0;
  }
`;

export default MaximizedPopup;
